// Title screen component - RPG-style main menu

import { css, html } from 'lit';
import { getBackendUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import '../game/music-controls.js';

/**
 * Full-viewport title screen with RPG-style vertical menu
 * @fires logout
 */
class TitleScreen extends BaseComponent {
  static properties = {
    user: { type: Object },
    hideMenu: { type: Boolean, attribute: 'hide-menu' },
    desktopDownloadUrl: { type: String },
    menuState: { type: String, attribute: 'menu-state' },
  };

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      flex: 1;
    }

    .title-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 0 1rem 5rem;
      background: center center / cover no-repeat;
      position: relative;
      overflow: hidden;
    }

    /* Dark overlay for text legibility */
    .title-screen::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.45);
      pointer-events: none;
    }

    .title-content {
      z-index: 1;
      text-align: center;
    }

    .game-title {
      font-family: 'MedievalSharp', Georgia, serif;
      font-size: 4.5rem;
      font-weight: 700;
      color: #ffffff;
      margin: 0 0 0.25rem 0;
      letter-spacing: 0.15em;
      text-shadow:
        0 0 8px rgba(0, 0, 0, 1),
        0 0 20px rgba(0, 0, 0, 0.9),
        0 0 40px rgba(0, 0, 0, 0.7),
        0 2px 4px rgba(0, 0, 0, 1);
    }

    .tagline {
      font-size: 1rem;
      color: rgba(255, 255, 255, 0.85);
      margin: 0 0 3rem 0;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      font-weight: 300;
      text-shadow:
        0 0 6px rgba(0, 0, 0, 1),
        0 0 16px rgba(0, 0, 0, 0.8);
    }

    .menu-slider {
      overflow: hidden;
      width: 400px;
      max-width: 100%;
      margin: 0 auto;
    }

    .menu-track {
      display: flex;
      transition: transform 0.3s ease;
      width: 200%;
    }

    .menu-panel {
      width: 50%;
      flex-shrink: 0;
    }

    .menu {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
    }

    .menu-btn {
      display: block;
      width: 280px;
      padding: 0.85rem 1.5rem;
      font-size: 1.1rem;
      font-family: 'MedievalSharp', Georgia, serif;
      letter-spacing: 0.12em;
      color: rgba(255, 255, 255, 0.9);
      background: rgba(0, 0, 0, 0.55);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: center;
      backdrop-filter: blur(4px);
    }

    .menu-btn:hover {
      background: rgba(0, 0, 0, 0.65);
      border-color: rgba(13, 110, 253, 0.5);
      color: #6ea8fe;
      text-shadow: 0 0 8px rgba(13, 110, 253, 0.3);
    }

    .menu-btn:active {
      background: rgba(0, 0, 0, 0.7);
      transform: scale(0.98);
    }

    .menu-btn.disabled {
      color: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.1);
      cursor: not-allowed;
      background: rgba(0, 0, 0, 0.35);
    }

    .menu-btn.disabled:hover {
      background: rgba(0, 0, 0, 0.35);
      border-color: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.3);
      text-shadow: none;
    }

    .submenu-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
      margin-bottom: 0.5rem;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .submenu-header:hover .back-arrow,
    .submenu-header:hover .submenu-label {
      color: #6ea8fe;
    }

    .back-arrow {
      color: rgba(255, 255, 255, 0.7);
      font-size: 1.8rem;
      transition: color 0.2s ease;
      text-shadow:
        0 0 8px rgba(0, 0, 0, 1),
        0 0 20px rgba(0, 0, 0, 0.9),
        0 0 40px rgba(0, 0, 0, 0.7),
        0 2px 4px rgba(0, 0, 0, 1);
    }

    .submenu-label {
      font-family: 'MedievalSharp', Georgia, serif;
      font-size: 2rem;
      color: rgba(255, 255, 255, 0.9);
      letter-spacing: 0.12em;
      transition: color 0.2s ease;
      text-shadow:
        0 0 8px rgba(0, 0, 0, 1),
        0 0 20px rgba(0, 0, 0, 0.9),
        0 0 40px rgba(0, 0, 0, 0.7),
        0 2px 4px rgba(0, 0, 0, 1);
    }

    .coming-soon-text {
      color: rgba(255, 255, 255, 0.85);
      font-size: 1.15rem;
      line-height: 1.6;
      text-align: center;
      max-width: 400px;
      margin: 0 auto;
      text-shadow:
        0 0 6px rgba(0, 0, 0, 1),
        0 0 16px rgba(0, 0, 0, 0.8);
    }

    .coming-soon-badge {
      display: inline-block;
      font-size: 1rem;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.5);
      border-radius: 4px;
      padding: 0.4rem 1rem;
      margin-bottom: 0.75rem;
    }

    .download-btn {
      text-decoration: none;
      font-size: 0.9rem;
      padding: 0.65rem 1.5rem;
      color: rgba(255, 255, 255, 0.5);
      border-color: rgba(255, 255, 255, 0.1);
      background: rgba(0, 0, 0, 0.35);
    }

    .download-btn:hover {
      color: rgba(255, 255, 255, 0.8);
      border-color: rgba(255, 255, 255, 0.2);
      background: rgba(0, 0, 0, 0.5);
      text-shadow: none;
    }

    .sign-in-info {
      margin-bottom: 1.5rem;
      text-align: center;
    }

    music-controls {
      position: absolute;
      top: 1rem;
      right: 1rem;
      bottom: auto;
      left: auto;
      z-index: 1;
    }

    .footer-info {
      position: absolute;
      bottom: 0.25rem;
      left: 1rem;
      right: 1rem;
      z-index: 1;
      font-size: 0.7rem;
      color: rgba(255, 255, 255, 0.85);
      letter-spacing: 0.2em;
      font-weight: 300;
      text-shadow:
        0 0 6px rgba(0, 0, 0, 1),
        0 0 16px rgba(0, 0, 0, 0.8);
    }

    /* In row mode (md+), equalize the side columns so the links are
       actually centered horizontally in the viewport. */
    @media (min-width: 768px) {
      .footer-info > span:first-child,
      .footer-info > span:last-child {
        flex: 1 1 0;
      }
      .footer-info > span:last-child {
        text-align: right;
      }
    }

    .footer-links {
      display: flex;
      justify-content: center;
      gap: 1.5rem;
    }

    .footer-links a {
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;
      cursor: pointer;
      transition: color 0.2s ease;
    }

    .footer-links a:hover {
      color: #ffffff;
      text-decoration: underline;
    }

    .footer-attribution {
      position: absolute;
      bottom: 1.5rem;
      left: 1rem;
      right: 1rem;
      z-index: 1;
      font-size: 0.7rem;
      color: rgba(255, 255, 255, 0.85);
      letter-spacing: 0.2em;
      font-weight: 300;
      text-align: center;
      text-shadow:
        0 0 6px rgba(0, 0, 0, 1),
        0 0 16px rgba(0, 0, 0, 0.8);
    }

    .footer-attribution a {
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;
      cursor: pointer;
      transition: color 0.2s ease;
    }

    .footer-attribution a:hover {
      color: #ffffff;
      text-decoration: underline;
    }
  `;

  constructor() {
    super();
    /** @type {import('../../types/jsdoc-types.js').User|null} */
    this.user = null;
    this.hideMenu = false;
    this.desktopDownloadUrl = '';
    /** @type {'main'|'play'} */
    this.menuState = 'main';
    /** @type {{version: string, gitCommit: string, buildDate: string}|null} */
    this._versionInfo = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._fetchVersion();
  }

  async _fetchVersion() {
    try {
      const response = await fetch(`${getBackendUrl()}/api/service/version`);
      if (response.ok) {
        this._versionInfo = await response.json();
        this.requestUpdate();
      }
    } catch {
      // Version display is not critical
    }
  }

  get _isAdmin() {
    return this.user?.permissions?.includes('operate:rpgish');
  }

  /** @param {string} path */
  _navigate(path) {
    router.navigate(path);
  }

  _renderSubmenu() {
    const COMING_SOON = {
      create: {
        label: 'Create',
        back: '/',
        description:
          "You've played our adventures, now make your own! Create will allow you to create adventures, monsters, spells, and entire campaigns.",
      },
      campaign: {
        label: 'Campaign',
        back: '/play',
        description:
          'Play through a series of connected adventures in the lands of ReplaceMe as your party seeks fortune and glory.',
      },
      survival: {
        label: 'Dungeon Survival',
        back: '/play',
        description:
          'Dungeon Survival pits you against other players as well as monsters and traps in a large, randomly-created dungeon to see who is the last PC standing.',
      },
    };

    const comingSoon = COMING_SOON[this.menuState];
    if (comingSoon) {
      return html`
        <div class="submenu-header" @click="${() => this._navigate(comingSoon.back)}">
          <span class="back-arrow">&larr;</span>
          <span class="submenu-label">${comingSoon.label}</span>
        </div>
        <div class="coming-soon-text">
          <div class="coming-soon-badge">Coming Soon</div>
          <p>${comingSoon.description}</p>
        </div>
      `;
    }

    return html`
      <div class="submenu-header" @click="${() => this._navigate('/')}">
        <span class="back-arrow">&larr;</span>
        <span class="submenu-label">Play</span>
      </div>
      <nav class="menu">
        <button class="menu-btn" @click="${() => this._navigate('/campaign')}">Campaign</button>
        <button class="menu-btn" @click="${() => this._navigate('/play/adventures')}">Adventures</button>
        <button class="menu-btn" @click="${() => this._navigate('/survival')}">Dungeon Survival</button>
        <button class="menu-btn" @click="${() => this._navigate('/characters')}">My Characters</button>
      </nav>
    `;
  }

  _handleLogin() {
    this.dispatchEvent(new CustomEvent('login', { bubbles: true }));
  }

  render() {
    return html`
      <div
        class="title-screen"
        style="background-image: url('${process.env.PUBLIC_CDN_BASE_URL}/title-screen/adventurers-at-the-barrow')"
      >
        <div class="title-content">
          <h1 class="game-title">RPGish</h1>
          <p class="tagline">Automated GM-Less 5e Adventures</p>
          <div style="${this.hideMenu ? 'opacity:0;pointer-events:none' : ''}">
          ${
            this.user
              ? html`
                  <div class="menu-slider">
                    <div class="menu-track" style="transform: translateX(${this.menuState !== 'main' ? '-50%' : '0'})">
                      <div class="menu-panel">
                        <nav class="menu">
                          <button class="menu-btn" @click="${() => this._navigate('/play')}">Play</button>
                          <button class="menu-btn" @click="${() => this._navigate('/create')}">Create</button>
                          <button class="menu-btn" @click="${() => this._navigate('/account')}">Account</button>
                          ${
                            this._isAdmin
                              ? html` <button class="menu-btn" @click="${() => this._navigate('/staff/users')}">Staff</button> `
                              : ''
                          }
                        </nav>
                      </div>
                      <div class="menu-panel">
                        ${this._renderSubmenu()}
                      </div>
                    </div>
                  </div>
                `
              : html`
                  <div class="sign-in-info">
                    <div class="alert alert-info mb-0">
                      <i class="bi bi-info-circle me-2"></i>RPGish is currently in closed testing. Sign in if you have
                      been invited to test.
                    </div>
                  </div>
                  <nav class="menu">
                    <button class="menu-btn" @click="${this._handleLogin}">Sign In</button>
                    ${
                      this.desktopDownloadUrl
                        ? html`
                          <a class="menu-btn download-btn" href="${this.desktopDownloadUrl}">
                            <i class="bi bi-apple"></i> Download for Mac
                          </a>
                        `
                        : ''
                    }
                  </nav>
                `
          }
          </div>
        </div>
        <music-controls></music-controls>
        <div
          class="footer-info d-flex flex-column flex-md-row align-items-center justify-content-md-between gap-1 gap-md-0"
        >
          <span>© 2026 Dice Driven Design LLC. RPGish™</span>
          <div class="footer-links">
            <a
              href="/terms"
              @click="${
                /** @param {Event} e */ (e) => {
                  e.preventDefault();
                  this._navigate('/terms');
                }
              }"
              >Terms of Service</a
            >
            <a
              href="/privacy"
              @click="${
                /** @param {Event} e */ (e) => {
                  e.preventDefault();
                  this._navigate('/privacy');
                }
              }"
              >Privacy Policy</a
            >
          </div>
          ${this._versionInfo ? html`<span>${this._versionInfo.version.split('+')[0]}</span>` : html`<span></span>`}
        </div>
        <div class="footer-attribution">
          <a
            href="/terms#open-game-content"
            @click="${
              /** @param {Event} e */ (e) => {
                e.preventDefault();
                this._navigate('/terms#open-game-content');
              }
            }"
            >Includes material from SRD 5.2.1 under CC BY 4.0</a
          >
        </div>
      </div>
    `;
  }
}

customElements.define('title-screen', TitleScreen);
